import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, RotateCcw, X, User } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, getStoredToken } from "@/lib/api";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function AdvisorChatWindow({ sessionPath, chatPath, newSessionPath, testIds, emptyStateText, disclaimer, contextEmployee, onClearContext }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  const loadSession = () => {
    api.get(sessionPath).then(({ data }) => setMessages(data.messages));
  };

  useEffect(loadSession, [sessionPath]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleNewSession = async () => {
    await api.post(newSessionPath);
    setMessages([]);
    toast.success("Nueva conversación iniciada");
  };

  const handleSend = async () => {
    if (!input.trim() || streaming) return;
    const userText = input;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userText }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const body = { message: userText };
      if (contextEmployee?.id) body.employee_id = contextEmployee.id;
      const response = await fetch(`${API_BASE}${chatPath}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getStoredToken()}` },
        body: JSON.stringify(body),
      });
      if (!response.ok || !response.body) throw new Error("stream failed");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let done = false;
      while (!done) {
        const { done: readerDone, value } = await reader.read();
        done = readerDone;
        buffer += decoder.decode(value || new Uint8Array(), { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const dataStr = part.slice(6);
          if (dataStr === "[DONE]") continue;
          const parsed = JSON.parse(dataStr);
          if (parsed.error) {
            toast.error(parsed.error);
            continue;
          }
          setMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + parsed.content };
            return next;
          });
        }
      }
    } catch (err) {
      toast.error("No se pudo conectar con el asesor. Intenta de nuevo.");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-card border border-petroleum/10 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-petroleum/10">
        <p className="text-sm font-semibold text-charcoal flex items-center gap-2"><Sparkles className="w-4 h-4 text-terracotta" /> Asesor IA</p>
        <button data-testid={testIds.newSessionButton} onClick={handleNewSession} className="text-xs text-charcoal/50 hover:text-petroleum flex items-center gap-1">
          <RotateCcw className="w-3 h-3" /> Nueva conversación
        </button>
      </div>

      {contextEmployee && (
        <div data-testid={testIds.contextChip} className="flex items-center justify-between gap-2 mx-4 mt-3 bg-petroleum/5 border border-petroleum/15 rounded-full px-3 py-1.5">
          <p className="text-xs text-petroleum flex items-center gap-1.5 truncate">
            <User className="w-3 h-3 shrink-0" /> Consultando sobre: <b className="truncate">{contextEmployee.name}</b>
          </p>
          <button data-testid={testIds.clearContextButton} onClick={onClearContext} className="text-petroleum/50 hover:text-petroleum shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {messages.length === 0 && <p className="text-sm text-charcoal/40 text-center mt-10">{emptyStateText}</p>}
        {messages.map((m, idx) => (
          <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              data-testid={m.role === "user" ? testIds.userBubble : testIds.assistantBubble}
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                m.role === "user" ? "bg-petroleum text-white whitespace-pre-wrap" : "bg-accent text-charcoal"
              }`}
            >
              {m.role === "assistant" ? (
                m.content ? (
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="mb-1.5 last:mb-0 leading-relaxed">{children}</p>,
                      strong: ({ children }) => <strong className="font-semibold text-charcoal">{children}</strong>,
                      ul: ({ children }) => <ul className="list-disc pl-4 mb-1.5 space-y-0.5">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal pl-4 mb-1.5 space-y-0.5">{children}</ol>,
                      li: ({ children }) => <li>{children}</li>,
                      h1: ({ children }) => <p className="font-semibold text-sm mb-1">{children}</p>,
                      h2: ({ children }) => <p className="font-semibold text-sm mb-1">{children}</p>,
                      h3: ({ children }) => <p className="font-semibold text-sm mb-1">{children}</p>,
                      a: ({ children, href }) => <a href={href} target="_blank" rel="noreferrer" className="underline text-petroleum">{children}</a>,
                    }}
                  >
                    {m.content}
                  </ReactMarkdown>
                ) : (
                  streaming && idx === messages.length - 1 ? "..." : ""
                )
              ) : (
                m.content
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="p-4 border-t border-petroleum/10">
        <div className="flex gap-2">
          <Textarea
            data-testid={testIds.messageInput}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="Escribe tu pregunta..."
            className="bg-white min-h-11 max-h-28 resize-none"
          />
          <Button data-testid={testIds.sendButton} onClick={handleSend} disabled={streaming || !input.trim()} className="bg-terracotta hover:bg-terracotta/90 rounded-full shrink-0 h-11 w-11 p-0">
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-charcoal/40 mt-2">{disclaimer}</p>
      </div>
    </div>
  );
}
